import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import classNames from "classnames";
import LazyLoad from "react-lazy-load";
import get from "lodash/get";

import Flag from "src/app/components/Flag/Flag";
import ImagesChecker from "src/app/components/ImagesChecker/ImagesChecker";
import personImage from "src/images/person.svg";
import { Img } from "src/app/components/Img/Img";
import { SocialProvider, convertTikTokToWorkspaceVersion } from "src/types";
import { gallerySocialProviders } from "src/app/components/Gallery/components/GalleryFiltersDropdown/utils";
import METHODS from "../../helpers/methods";
import { CDTableColumn, CDTableRow } from "../../components/CDTable/CDTable";
import InfluencerInfo from "../../components/InfluencerInfo/InfluencerInfo";
import AudiencesResultsCheckboxWrapper from "../AudiencesResults/components/AudiencesResultsCheckboxWrapper";
import AudiencesResultsCheckbox from "../AudiencesResults/components/AudiencesResultsCheckbox";
import { GoToSocial } from "../components/GoToSocial";
import CustomCheckbox from "src/app/components/CustomCheckbox/CustomCheckbox";
import ViralPostsIndicator from "src/app/components/ViralPostsIndicator/ViralPostsIndicator";
import {
  STICKY_COLUMN_WIDTHS,
  STICKY_OFFSETS,
} from "./utils/getCreatorResultsColumns";

function GenericCreatorCell({ influencer, columnKey, path }) {
  const value = get(influencer, path);

  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (columnKey === "country") {
    const regionNamesInEnglish = new Intl.DisplayNames(["en"], {
      type: "region",
    });
    try {
      return regionNamesInEnglish.of(value);
    } catch {
      return value;
    }
  }

  return value;
}

// Cells rendered before the data-driven columns (checkbox + photo preview).
const LEADING_COLUMNS_COUNT = 2;

// Relevance values at or above this threshold are highlighted as high-relevance.
const RELEVANCE_HIGH_THRESHOLD = 70;

export default function CreatorResultRow({
  influencer,
  index,
  columns = [],
  selectUserClick,
  onRangeSelect,
  lastSelectedIndex,
  setLastSelectedIndex,
  highlightedCellKeys,
  activeResult,
  searchData,
}) {
  const headerRef = useRef(null);
  const history = useHistory();

  const selectedInfluencersOnResults = useSelector(
    (state) => state.creatorSearchReducer.selectedInfluencersOnResults,
  );

  const isSelected = selectedInfluencersOnResults.includes(influencer.userId);

  const isCellHighlighted = (columnIndex) =>
    highlightedCellKeys.has(`${index}-${columnIndex}`);
  const isCellActive = (columnIndex) =>
    activeResult?.rowIndex === index &&
    activeResult?.columnIndex === columnIndex;

  const openProfile = () => {
    const pathNameWithProfile = `${window.location.pathname}/profile/${influencer.socialProvider}/${influencer.socialUsername}`;
    const searchParams = new URLSearchParams(window.location.search);
    const shouldShowMoreByDefault = gallerySocialProviders.find(
      (provider) =>
        provider.value === influencer.socialProvider.replace("_", ""),
    );
    if (shouldShowMoreByDefault) {
      searchParams.set("galleryShowMoreByDefault", true);
    }
    const search = searchParams.toString();

    history.push({
      pathname: pathNameWithProfile,
      search,
    });
  };

  const renderColumnCell = (columnKey, path, type) => {
    if (columnKey === "name") {
      return (
        <InfluencerInfo
          socialProvider={influencer.socialProvider}
          socialProviders={influencer.socialProviders}
          username={influencer.socialUsername}
          headerRef={headerRef}
          wsGlobalTaskUuid={influencer?.wsGlobalTaskUuid}
          influencerName={influencer?.name}
          creatorSearchSelectedProviders={searchData?.selectedProviders ?? []}
        />
      );
    }

    switch (type) {
      case "number": {
        const numericValue = get(influencer, path);

        if (
          numericValue === null ||
          numericValue === undefined ||
          numericValue === ""
        ) {
          return "-";
        }

        return METHODS.numbersWithPostfixNumbers(numericValue);
      }

      case "posts":
        return (
          <LazyLoad>
            <div
              className="audience-results__recent-posts"
              onClick={openProfile}
            >
              <ImagesChecker
                imageClassName="cdt-table__image-content post-preview"
                data={influencer.lastPosts || []}
                accessorKey="thumbnailSrc"
                // TikTok-only HEIC recovery, see task:
                // https://app.asana.com/1/242052987772680/project/807220246535156/task/1217546080711977?focus=true
                allowHeicRecovery={
                  convertTikTokToWorkspaceVersion(influencer.socialProvider) ===
                  SocialProvider.TikTok
                }
                // Rendered at 80px (.cdt-table__image-content in CreatorResults.scss).
                heicRecoveryMaxDimension={80}
                noImagesFallback={
                  <GoToSocial
                    socialProfileUrl={influencer.profileUrl}
                    socialProvider={influencer.socialProvider}
                  />
                }
              />
            </div>
          </LazyLoad>
        );
      case "percent": {
        // avgErPercent comes with thousands separator, e.g. "1,234.5"
        const avgEr = String(influencer.avgErPercent ?? "").replace(/,/g, "");

        if (!Number(avgEr)) {
          return "-";
        }

        return (
          <span className="viral-posts-er-cell">
            {influencer.avgErPercent}%
            <ViralPostsIndicator
              er={influencer.avgErPercent}
              erWithVirals={influencer.avgErWithViralsPercent}
              viralPostsCount={influencer.viralPostsCount}
              erPostsCount={influencer.erPostsCount}
              alignRight
            />
          </span>
        );
      }
      case "percentage": {
        const percentageValue = Number(get(influencer, path));

        if (!Number.isFinite(percentageValue)) {
          return "-";
        }

        return `${Math.round(percentageValue)}%`;
      }
      case "relevance": {
        const relevanceValue = influencer.velocityIndex || 0;

        return (
          <span
            className={classNames("relevance-value", {
              "relevance-value--high":
                relevanceValue >= RELEVANCE_HIGH_THRESHOLD,
            })}
          >
            {relevanceValue}%
          </span>
        );
      }
      case "boolean": {
        const boolValue = Boolean(get(influencer, path));
        return <CustomCheckbox checked={boolValue} readOnly disabled />;
      }
      default:
        return (
          <GenericCreatorCell
            influencer={influencer}
            columnKey={columnKey}
            path={path}
          />
        );
    }
  };

  return (
    <CDTableRow highlighted={isSelected}>
      <CDTableColumn
        className="cdt-table__checkbox"
        style={{
          position: "sticky",
          left: STICKY_OFFSETS.selectPage,
          width: STICKY_COLUMN_WIDTHS.selectPage,
        }}
        data-sticky-td
      >
        <AudiencesResultsCheckboxWrapper>
          <AudiencesResultsCheckbox
            influencer={influencer}
            selectedInfluencers={selectedInfluencersOnResults}
            selectUserClick={selectUserClick}
            handleRangeSelect={onRangeSelect}
            lastSelectedIndex={lastSelectedIndex}
            setLastSelectedIndex={setLastSelectedIndex}
            index={index}
          />
        </AudiencesResultsCheckboxWrapper>
      </CDTableColumn>
      <CDTableColumn
        style={{
          position: "sticky",
          left: STICKY_OFFSETS.preview,
          width: STICKY_COLUMN_WIDTHS.preview,
        }}
        data-sticky-td
      >
        <LazyLoad offset={300}>
          <div className="influencer-photo">
            <Img
              className={classNames(
                "influencer-photo__img",
                "cdt-table__image-content",
                "cdt-table__image",
              )}
              src={influencer.profilePictureUrl}
              onClick={() => headerRef.current.click()}
              fallbackImageSrc={personImage}
            />
            {influencer.country && <Flag countryCode={influencer.country} />}
          </div>
        </LazyLoad>
      </CDTableColumn>
      {columns.map((column, columnPosition) => {
        const cellIndex = columnPosition + LEADING_COLUMNS_COUNT;

        const isNameColumn = column.key === "name";

        return (
          <React.Fragment key={column.key}>
            <CDTableColumn
              className={classNames("to-left", {
                "table-cell-highlighted": isCellHighlighted(cellIndex),
                "table-cell-highlighted-active": isCellActive(cellIndex),
                "cdt-table__cell--country": column.key === "country",
                "cdt-table__cell--name": isNameColumn,
              })}
              style={
                isNameColumn
                  ? { position: "sticky", left: STICKY_OFFSETS.name }
                  : undefined
              }
              data-sticky-td={isNameColumn || undefined}
              data-sticky-last-left-td={isNameColumn || undefined}
            >
              {renderColumnCell(column.key, column.path, column.type)}
            </CDTableColumn>
            {isNameColumn && (
              <CDTableColumn className="cdt-table__name-spacer" />
            )}
          </React.Fragment>
        );
      })}
      <CDTableColumn className="cdt-table__spacer" />
    </CDTableRow>
  );
}
